import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  listStores,
  getStore,
  createStore,
  updateStore,
  deleteStore,
} from '../services/adminStoreService';
import { listUsers } from '../services/adminUserService';
import StoreLocationPicker from '../components/StoreLocationPicker';
import AdminAuthService from '../services/adminAuthService';
import { logActivity } from '../services/adminActivityService';
import { can } from '../utils/adminPermissions';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { STORE_TABLE_ID } from '../lib/appwrite';
import './StoresPage.css';

const STORE_SIZES = ['small', 'medium', 'large'];

const StoresPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState(''); // '', 'active', 'inactive'
  const [modal, setModal] = useState(null); // null | 'create' | 'edit' | 'view'
  const [selectedStore, setSelectedStore] = useState(null);
  const [formData, setFormData] = useState(getInitialFormData());
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  function getInitialFormData() {
    return {
      storeName: '',
      ownerId: '',
      storeSize: 'small',
      district: '',
      region: '',
      ward: '',
      street: '',
      road: '',
      buildingNumber: '',
      tin: '',
      surveyed: 'no',
      contact: '',
      businessNumber: '',
      unsurveyedAddress: '',
      coordinates: null,
      isActive: false,
    };
  }

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [storesRes, usersRes, role] = await Promise.all([
        listStores(),
        listUsers(),
        AdminAuthService.getAdminRole(),
      ]);
      setStores(storesRes || []);
      setUsers(usersRes || []);
      setAdminRole(role);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [STORE_TABLE_ID] });

  useEffect(() => {
    const editId = location.state?.editStoreId;
    if (editId && stores.length > 0) {
      const store = stores.find((s) => s.$id === editId);
      if (store) {
        setSelectedStore(store);
        setFormData({
          storeName: store.storeName || '',
          ownerId: store.ownerId || '',
          storeSize: store.storeSize || 'small',
          district: store.district || '',
          region: store.region || '',
          ward: store.ward || '',
          street: store.street || '',
          road: store.road || '',
          buildingNumber: store.buildingNumber || '',
          tin: store.tin || '',
          surveyed: store.surveyed || 'no',
          contact: store.contact || '',
          businessNumber: store.businessNumber || '',
          unsurveyedAddress: store.unsurveyedAddress || '',
          coordinates: store.coordinates || null,
          isActive: !!store.isActive,
        });
        setModal('edit');
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.editStoreId, location.pathname, stores, navigate]);

  const ownerMap = users.reduce((acc, u) => {
    const id = u.uid || u.$id;
    if (id) acc[id] = [u.fname, u.lname].filter(Boolean).join(' ') || u.email || id;
    return acc;
  }, {});

  const filteredStores = stores.filter((s) => {
    const matchSearch =
      !search ||
      (s.storeName || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.region || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.district || '').toLowerCase().includes(search.toLowerCase()) ||
      (ownerMap[s.ownerId] || '').toLowerCase().includes(search.toLowerCase());
    const matchActive =
      filterActive === '' ||
      (filterActive === 'active' && s.isActive) ||
      (filterActive === 'inactive' && !s.isActive);
    return matchSearch && matchActive;
  });

  const openCreate = () => {
    setFormData(getInitialFormData());
    setSelectedStore(null);
    setModal('create');
  };

  const openEdit = (store) => {
    setSelectedStore(store);
    setFormData({
      storeName: store.storeName || '',
      ownerId: store.ownerId || '',
      storeSize: store.storeSize || 'small',
      district: store.district || '',
      region: store.region || '',
      ward: store.ward || '',
      street: store.street || '',
      road: store.road || '',
      buildingNumber: store.buildingNumber || '',
      tin: store.tin || '',
      surveyed: store.surveyed || 'no',
      contact: store.contact || '',
      businessNumber: store.businessNumber || '',
      unsurveyedAddress: store.unsurveyedAddress || '',
      coordinates: store.coordinates || null,
      isActive: !!store.isActive,
    });
    setModal('edit');
  };

  const openView = (store) => {
    navigate(`/dashboard/stores/${store.$id}`);
  };

  const closeModal = () => {
    setModal(null);
    setSelectedStore(null);
    setDeleteConfirm(null);
  };

  const handleSaveCreate = async (e) => {
    e.preventDefault();
    if (!formData.ownerId) {
      setError('Please select an owner (seller)');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await createStore(formData);
      await logActivity({
        action: 'create',
        resourceType: 'store',
        resourceId: res?.$id || res?.storeId || formData.storeId,
        details: `Created store: ${formData.storeName || 'New Store'}`,
      });
      closeModal();
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to create store');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedStore?.$id) return;
    try {
      setSaving(true);
      setError(null);
      await updateStore(selectedStore.$id, formData);
      await logActivity({
        action: 'update',
        resourceType: 'store',
        resourceId: selectedStore.$id,
        details: `Updated store: ${formData.storeName || selectedStore.storeName}`,
      });
      closeModal();
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to update store');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (store) => {
    try {
      setError(null);
      await updateStore(store.$id, { isActive: !store.isActive });
      await logActivity({
        action: 'update',
        resourceType: 'store',
        resourceId: store.$id,
        details: `Toggled store active to ${!store.isActive}`,
      });
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to update store');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm?.$id) return;
    try {
      setSaving(true);
      setError(null);
      await deleteStore(deleteConfirm.$id);
      await logActivity({
        action: 'delete',
        resourceType: 'store',
        resourceId: deleteConfirm.$id,
        details: `Deleted store: ${deleteConfirm.storeName || deleteConfirm.$id}`,
      });
      closeModal();
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      setError(err.message || 'Failed to delete store');
    } finally {
      setSaving(false);
    }
  };

  const sellers = users.filter(
    (u) => (u.roles || u.role || '').toLowerCase() === 'seller'
  );

  const canCreate = adminRole && can(adminRole, 'create', 'stores');
  const canUpdate = adminRole && can(adminRole, 'update', 'stores');
  const canApprove = adminRole && (can(adminRole, 'update', 'stores') || can(adminRole, 'approve', 'stores'));
  const canDelete = adminRole && can(adminRole, 'delete', 'stores');

  if (loading) {
    return (
      <div className="stores-loading">
        <div className="dashboard-spinner" />
        <p>Loading stores...</p>
      </div>
    );
  }

  return (
    <div className="stores-page">
      <div className="stores-header">
        <div>
          <h1 className="dashboard-title">Stores</h1>
          <p className="dashboard-subtitle">
            Manage all stores on the platform. View, edit, activate, or delete.
          </p>
        </div>
        {canCreate && (
          <button className="stores-btn-primary" onClick={openCreate}>
            <img src="/icons/add-circle.svg" alt="" className="stores-btn-icon" aria-hidden />
            Add Store
          </button>
        )}
      </div>

      {error && (
        <div className="stores-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="stores-toolbar">
        <div className="stores-search-wrap">
          <img src="/icons/Search.svg" alt="" className="stores-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="Search by name, region, district, owner..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="stores-search"
          />
        </div>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
          className="stores-filter"
        >
          <option value="">All stores</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      <div className="stores-table-wrap">
        <table className="stores-table">
          <thead>
            <tr>
              <th>Store</th>
              <th>Owner</th>
              <th>Region</th>
              <th>Status</th>
              <th>Size</th>
              <th className="stores-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStores.length === 0 ? (
              <tr>
                <td colSpan={6} className="stores-empty">
                  No stores found.
                </td>
              </tr>
            ) : (
              filteredStores.map((store) => (
                <tr key={store.$id}>
                  <td>
                    <button
                      type="button"
                      className="stores-link"
                      onClick={() => openView(store)}
                    >
                      {store.storeName || 'Unnamed Store'}
                    </button>
                  </td>
                  <td>{ownerMap[store.ownerId] || store.ownerId || '—'}</td>
                  <td>{store.region || '—'}</td>
                  <td>
                    <span
                      className={`stores-badge ${store.isActive ? 'stores-badge-active' : 'stores-badge-inactive'}`}
                    >
                      {store.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{store.storeSize || '—'}</td>
                  <td className="stores-col-actions">
                    <button
                      type="button"
                      className="stores-btn-sm"
                      onClick={() => openView(store)}
                      title="View"
                    >
                      <img src="/icons/eye.svg" alt="" aria-hidden />
                      View
                    </button>
                    {canUpdate && (
                      <button
                        type="button"
                        className="stores-btn-sm stores-btn-edit"
                        onClick={() => openEdit(store)}
                        title="Edit"
                      >
                        <img src="/icons/pencil-edit-02.svg" alt="" aria-hidden />
                        Edit
                      </button>
                    )}
                    {canApprove && (
                      <button
                        type="button"
                        className={`stores-btn-sm ${store.isActive ? 'stores-btn-warn' : 'stores-btn-success'}`}
                        onClick={() => handleToggleActive(store)}
                        title={store.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {store.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="stores-btn-sm stores-btn-danger"
                        onClick={() => setDeleteConfirm(store)}
                        title="Delete"
                      >
                        <img src="/icons/delete-02.svg" alt="" aria-hidden />
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Modal */}
      {(modal === 'create' || modal === 'edit') && (
        <div className="stores-modal-overlay" onClick={closeModal}>
          <div
            className="stores-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="stores-modal-header">
              <h2>{modal === 'create' ? 'Add Store' : 'Edit Store'}</h2>
              <button type="button" className="stores-modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form
              onSubmit={modal === 'create' ? handleSaveCreate : handleSaveEdit}
              className="stores-form"
            >
              <div className="stores-form-grid">
                <div className="stores-form-field">
                  <label htmlFor="storeName">Store name *</label>
                  <input
                    id="storeName"
                    required
                    value={formData.storeName}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, storeName: e.target.value }))
                    }
                  />
                </div>
                <div className="stores-form-field">
                  <label htmlFor="ownerId">Owner (seller) *</label>
                  <select
                    id="ownerId"
                    required
                    value={formData.ownerId}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, ownerId: e.target.value }))
                    }
                    disabled={modal === 'edit'}
                  >
                    <option value="">Select owner</option>
                    {sellers.length === 0 ? (
                      <option value="" disabled>
                        No sellers found — users must have role &quot;seller&quot;
                      </option>
                    ) : (
                      sellers.map((u) => {
                        const id = u.uid || u.$id;
                        const name =
                          [u.fname, u.lname].filter(Boolean).join(' ') ||
                          u.email ||
                          id;
                        return (
                          <option key={id} value={id}>
                            {name}
                          </option>
                        );
                      })
                    )}
                  </select>
                  {modal === 'edit' && (
                    <small className="stores-form-hint">
                      Owner cannot be changed after creation
                    </small>
                  )}
                </div>
                <div className="stores-form-field">
                  <label htmlFor="storeSize">Store size</label>
                  <select
                    id="storeSize"
                    value={formData.storeSize}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, storeSize: e.target.value }))
                    }
                  >
                    {STORE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="stores-form-field">
                  <label htmlFor="tin">TIN *</label>
                  <input
                    id="tin"
                    value={formData.tin}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, tin: e.target.value }))
                    }
                    placeholder="Tax Identification Number"
                  />
                </div>
                <div className="stores-form-field stores-form-full">
                  <label>Store location</label>
                  <StoreLocationPicker
                    value={{
                      surveyed: formData.surveyed,
                      region: formData.region,
                      district: formData.district,
                      ward: formData.ward,
                      street: formData.street,
                      road: formData.road,
                      buildingNumber: formData.buildingNumber,
                      unsurveyedAddress: formData.unsurveyedAddress,
                      coordinates: formData.coordinates,
                    }}
                    onChange={(loc) =>
                      setFormData((f) => ({
                        ...f,
                        surveyed: loc.surveyed || 'no',
                        region: loc.region || '',
                        district: loc.district || '',
                        ward: loc.ward || '',
                        street: loc.street || '',
                        road: loc.road || '',
                        buildingNumber: loc.buildingNumber || '',
                        unsurveyedAddress: loc.unsurveyedAddress || '',
                        coordinates: loc.coordinates || null,
                      }))
                    }
                    disabled={saving}
                  />
                </div>
                <div className="stores-form-field">
                  <label htmlFor="contact">Contact</label>
                  <input
                    id="contact"
                    value={formData.contact}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, contact: e.target.value }))
                    }
                    placeholder="Phone or email"
                  />
                </div>
                <div className="stores-form-field">
                  <label htmlFor="businessNumber">Business number</label>
                  <input
                    id="businessNumber"
                    value={formData.businessNumber}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        businessNumber: e.target.value,
                      }))
                    }
                  />
                </div>
                {modal === 'edit' && (
                  <div className="stores-form-field stores-form-check">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData((f) => ({ ...f, isActive: e.target.checked }))
                        }
                      />
                      Active (visible to customers)
                    </label>
                  </div>
                )}
              </div>
              <div className="stores-modal-footer">
                <button
                  type="button"
                  className="stores-btn-secondary"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="stores-btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : modal === 'create' ? 'Create Store' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="stores-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div
            className="stores-modal stores-modal-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Delete store?</h3>
            <p>
              This will permanently delete &quot;{deleteConfirm.storeName}&quot; and cannot be
              undone. Products, orders, and related data may be affected.
            </p>
            <div className="stores-modal-footer">
              <button
                type="button"
                className="stores-btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="stores-btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoresPage;
